import { Builder } from '@builder.io/react'
import { faChevronLeft, faClose } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Dialog, Transition } from '@headlessui/react'
import React, { useState } from 'react'

interface Props {
  images: { imgSrc: string; altText: string }[]
  maxNoImg: number
}

const Lightbox = ({ images = [], maxNoImg = 5 }: Props) => {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [showModal, setShowModal] = useState(false)
  return (
    <>
      <div className="container m-auto flex w-full items-center justify-center gap-x-2 p-4">
        {images.length > 0 &&
          images.slice(0, maxNoImg || 5).map((item, index) => {
            return (
              <div>
                <img
                  src={item.imgSrc}
                  className="h-full w-full max-w-xs cursor-pointer transition delay-300 hover:shadow"
                  onClick={() => {
                    setShowModal(true)
                    setCurrentSlide(index)
                  }}
                />
              </div>
            )
          })}
      </div>
      <Transition.Root show={showModal} as={'div'}>
        <Dialog as="div" className="fixed inset-0 z-50 overflow-y-auto" onClose={() => setShowModal(false)}>
          <div className="m-auto flex h-screen items-center justify-center p-8 text-center md:w-9/12 xl:w-7/12">
            <Transition.Child
              onClick={() => setShowModal(false)}
              as={'div'}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-80 transition-opacity" />
              <FontAwesomeIcon
                icon={faClose}
                fontSize={40}
                className="absolute top-4 right-4 cursor-pointer text-white md:top-8 md:right-8"
                onClick={() => setShowModal(false)}
              />
            </Transition.Child>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">
              &#8203;
            </span>
            <Transition.Child
              as={'div'}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="relative">
                <div className="absolute top-0 px-3 py-2 text-xs !text-gray-300 !mix-blend-difference sm:text-base">
                  {currentSlide + 1} / {images?.length}
                </div>
                <img
                  src={images?.[currentSlide]?.imgSrc || ''}
                  alt={images?.[currentSlide]?.altText || ''}
                  className="w-full"
                />
                <p
                  className="absolute left-0 top-1/2 -translate-y-1/2 cursor-pointer p-2 text-3xl text-white hover:bg-black hover:bg-opacity-60"
                  onClick={() =>
                    currentSlide === 0 ? setCurrentSlide(images.length - 1) : setCurrentSlide(currentSlide - 1)
                  }
                >
                  &#10094;
                </p>
                <p
                  className="absolute right-0 top-1/2 -translate-y-1/2 cursor-pointer p-2 text-3xl text-white hover:bg-black hover:bg-opacity-60"
                  onClick={() =>
                    currentSlide === images.length - 1 ? setCurrentSlide(0) : setCurrentSlide(currentSlide + 1)
                  }
                >
                  &#10095;
                </p>
              </div>

              <div className="py-1 px-4  text-center font-bold">
                <p className="text-xs !text-white sm:text-base">{images?.[currentSlide]?.altText || ''}</p>
              </div>
              <div className="m-auto flex w-full items-center justify-center gap-x-2">
                {images.slice(0, maxNoImg || 5).map((item, index) => {
                  return (
                    <div>
                      <img
                        src={item.imgSrc}
                        className="h-full w-full max-w-xs cursor-pointer opacity-60 hover:opacity-100 active:opacity-100"
                        onClick={() => {
                          setShowModal(true)
                          setCurrentSlide(index)
                        }}
                      />
                    </div>
                  )
                })}
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  )
}

export default Lightbox

Builder.registerComponent(Lightbox, {
  name: 'Lightbox',
  inputs: [
    {
      name: 'images',
      type: 'list',
      subFields: [
        { name: 'imgSrc', type: 'file', defaultValue: '' },
        { name: 'altText', type: 'text', defaultValue: '' }
      ],
      defaultValue: [{ imgSrc: '', altText: '' }]
    },
    {
      name: 'maxNoImg',
      type: 'number',
      defaultValue: 5
    }
  ]
})
